import { addQueryParamsUrl, BznavAppIds, IS_ANDROID, requestPostFetch, requestFetch, requestGetFetch, META_PIXEL_BASIC_PROPERTIES } from '@repo/common-utils';
import { sendBznavNativeMessage, UtmData } from '@repo/platform';

import { BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY } from '../constants';
import { SignUpPayload, TermsAgreementPayload, SignPaths } from '../types';
import { removeReturnUrl } from '../utils';

import { BaseAuthService } from './BaseAuthService';

export class SignService extends BaseAuthService {
  constructor(appId: BznavAppIds, clientId: string, clientSecret: string, signPaths: SignPaths) {
    super(appId, clientId, clientSecret, signPaths, process.env.NEXT_PUBLIC_SSO_API_SERVER ?? 'https://sso.dev.api.zent.kr');
  }

  startSocialSignIn(socialType: string) {
    const redirectUri = `${process.env.NEXT_PUBLIC_SSO_API_SERVER}/v2/auth/${socialType}/login`;
    const state = encodeURIComponent(
      JSON.stringify({
        appId: this._appId,
        clientId: this._clientId,
        isBznavApp: this._clientId.endsWith('-app'),
        sessionId: this.sessionId,
        clientSessionId: this.clientSessionId,
        extId: typeof window !== 'undefined' ? sessionStorage.getItem(BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY) || '' : '',
        returnUrl: {
          domain: typeof window !== 'undefined' ? window.location.origin : '',
          success: this.signInSuccessUrl,
          error: this._signPaths.signIn,
          signUp: this._signPaths.signUpTerms
        }
      })
    );
    const restAPIKey = (socialType === 'kakao' ? process.env.NEXT_PUBLIC_KAKAO_REST_API_KEY : process.env.NEXT_PUBLIC_NAVER_REST_API_KEY) as string;
    const loginParams: Record<string, string> = { response_type: 'code', redirect_uri: redirectUri, client_id: restAPIKey, state };
    const urlSearchParams = new URLSearchParams(loginParams);
    if (this._clientId.endsWith('-app') && !(IS_ANDROID && socialType === 'apple')) {
      sendBznavNativeMessage('startSocialSignIn', {
        loginType: socialType,
        params: loginParams
      });
    } else {
      if (socialType === 'kakao') {
        window.location.replace(`https://kauth.kakao.com/oauth/authorize?${urlSearchParams.toString()}`);
      } else if (socialType === 'naver') {
        window.location.replace(`https://nid.naver.com/oauth2.0/authorize?${urlSearchParams.toString()}`);
      } else if (socialType === 'apple') {
        window.AppleID.auth.init({
          clientId: 'com.bznav.sso',
          redirectURI: redirectUri,
          state,
          scope: 'name email',
          usePopup: false
        });
        window.AppleID.auth.signIn();
      } else {
        console.error('Unsupported login type:', socialType);
      }
    }
  }

  async startEmailSignIn(email: string, password: string) {
    const response = await requestPostFetch({
      url: `${this._authSeverUrl}/signin/v2`,
      data: {
        appId: this._appId,
        clientId: this._clientId,
        extId: typeof window !== 'undefined' ? sessionStorage.getItem(BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY) || '' : '',
        email,
        password
      }
    });
    const signInResult = await response.json();
    if (signInResult.result) {
      return addQueryParamsUrl(this.signInSuccessUrl, { code: signInResult.code, grant_type: 'authorization_code' });
    }
    return null;
  }

  async startSignUp(signUpData: SignUpPayload) {
    const metaProperties = sessionStorage.getItem(META_PIXEL_BASIC_PROPERTIES) ?? undefined;
    const response = await requestPostFetch({
      url: `${this._authSeverUrl}/signup/v2`,
      data: {
        appId: this._appId,
        clientId: this._clientId,
        extId: typeof window !== 'undefined' ? sessionStorage.getItem(BZNAV_PARTNER_EXTERNAL_ID_SESSION_KEY) || '' : '',
        metaProperties,
        ...signUpData
      }
    });
    const signUpResult = await response.json();
    if (signUpResult.result) {
      const returnUrl = addQueryParamsUrl(this.signInSuccessUrl, { code: signUpResult.code, grant_type: signUpResult.grantType ?? 'authorization_code' });
      removeReturnUrl();
      return returnUrl;
    }
    return null;
  }

  async addSignedApp(terms: TermsAgreementPayload, utmData?: UtmData) {
    const metaProperties = sessionStorage.getItem(META_PIXEL_BASIC_PROPERTIES) ?? undefined;
    const response = await requestPostFetch({
      url: `${this._authSeverUrl}/add-signed-app`,
      data: {
        appId: this._appId,
        clientId: this._clientId,
        terms,
        metaProperties,
        ...utmData
      },
      config: {
        authorization: `Bearer ${this.accessToken}`
      }
    });
    const addSignResult = await response.json();
    if (addSignResult.result) {
      if (this.userData) {
        this.userData = {
          ...this.userData,
          signAppIds: [...(this.userData?.signAppIds || []), this._appId]
        };
      }
      const returnUrl = this.signInSuccessUrl;
      removeReturnUrl();
      return returnUrl;
    }
    return null;
  }

  getWithdrawReasons() {
    return requestGetFetch({ url: `${this._authSeverUrl}/my/leave-reasons?service_name=${this._appId}` });
  }

  startWithdraw(verificationCode: string, leaveReasonId: string, reason?: string) {
    return requestFetch(
      `${this._authSeverUrl}/withdraw`,
      { verificationCode, leaveReasonId, reason },
      {
        method: 'DELETE'
      }
    );
  }

  async checkEmailExists(email: string) {
    const response = await requestPostFetch({
      url: `${this._authSeverUrl}/check/email/v2`,
      data: {
        appId: this._appId,
        clientId: this._clientId,
        email
      }
    });
    const checkResult = await response.json();
    return checkResult.result;
  }

  sendPasswordResetEmail(email: string) {
    return requestPostFetch({
      url: `${this._authSeverUrl}/password/forgot/v2`,
      data: {
        appId: this._appId,
        clientId: this._clientId,
        email,
        resetUrl: typeof window !== 'undefined' ? `${window.location.origin}${this._signPaths.resetPassword}` : ''
      }
    });
  }

  resetPassword(passwordResetCode: string, password: string) {
    return requestPostFetch({
      url: `${this._authSeverUrl}/password/reset`,
      data: {
        password,
        passwordResetCode
      }
    });
  }
}
